(function(w) {
  'use strict';
  
  // Check dependencies
  if (!Pg.Utils) {
    console.warn('⚠️ Pg.Memory requires Pg.Utils - some features may not work');
  }

  // Pg.Memory module
  Pg.Memory = {
    // Tracked resources
    blobs: new Map(),           // url -> {id, blob, created, size}
    timers: new Set(),          // timer IDs
    listeners: new Map(),       // element -> [listeners]
    observers: new Set(),       // observer instances
    intervals: new Set(),       // interval IDs
    animationFrames: new Set(), // RAF IDs
    
    // Memory limits and settings
    limits: {
      maxBlobs: 100,           // Maximum tracked blobs
      maxBlobAge: 600000,      // 10 minutes in ms
      maxMemoryMB: 500,        // 500MB total
      maxImageSize: 50 * 1024 * 1024, // 50MB per image
      cleanupInterval: 300000  // 5 minutes cleanup interval
    },

    // Memory metrics
    metrics: {
      totalBlobsCreated: 0,
      totalBlobsRevoked: 0,
      totalMemoryUsed: 0,
      totalCleanups: 0,
      memoryWarnings: 0,
      lastCleanup: null,
      peakMemoryUsage: 0
    },

    // ================================================================
    // BLOB URL MANAGEMENT
    // ================================================================
    
    // Create tracked blob URL
    createBlob(blob, id = null) {
      try {
        if (!blob || typeof blob.size === 'undefined') {
          throw new Error('Invalid blob provided');
        }

        const url = URL.createObjectURL(blob);
        const blobId = id || this._generateBlobId();
        
        // Track the blob
        const blobInfo = {
          id: blobId,
          blob: blob,
          url: url,
          size: blob.size,
          type: blob.type,
          created: Date.now(),
          accessed: Date.now(),
          accessCount: 0
        };
        
        this.blobs.set(url, blobInfo);
        
        // Update metrics
        this.metrics.totalBlobsCreated++;
        this.metrics.totalMemoryUsed += blob.size;
        this.metrics.peakMemoryUsage = Math.max(
          this.metrics.peakMemoryUsage, 
          this.metrics.totalMemoryUsed
        );
        
        // Check memory limits
        this._checkMemoryLimits();
        
        cLog(`🔗 Created blob: ${blobId} (${this._formatSize(blob.size)})`);
        
        return url;
        
      } catch (error) {
        console.error('❌ Failed to create blob URL:', error);
        return null;
      }
    },

    // Revoke tracked blob URL
    revokeBlob(url) {
      try {
        if (!url || typeof url !== 'string') return false;
        
        const blobInfo = this.blobs.get(url);
        if (!blobInfo) {
          console.warn(`⚠️ Blob not tracked: ${url.substring(0, 50)}...`);
          return false;
        }
        
        // Revoke the URL
        URL.revokeObjectURL(url);
        
        // Update metrics
        this.metrics.totalBlobsRevoked++;
        this.metrics.totalMemoryUsed -= blobInfo.size;
        
        // Remove from tracking
        this.blobs.delete(url);
        
        cLog(`🗑️ Revoked blob: ${blobInfo.id} (${this._formatSize(blobInfo.size)})`);
        
        return true;
        
      } catch (error) {
        console.error('❌ Failed to revoke blob URL:', error);
        return false;
      }
    },

    // Mark blob as accessed (for LRU cleanup)
    accessBlob(url) {
      const blobInfo = this.blobs.get(url);
      if (blobInfo) {
        blobInfo.accessed = Date.now();
        blobInfo.accessCount++;
      }
    },

    // Get blob info
    getBlobInfo(url) {
      return this.blobs.get(url) || null;
    },

    // List all tracked blobs
    listBlobs() {
      return Array.from(this.blobs.values());
    },

    // ================================================================
    // TIMER MANAGEMENT
    // ================================================================
    
    // Track setTimeout
    setTimeout(callback, delay, ...args) {
      const timerId = w.setTimeout(() => {
        this.timers.delete(timerId);
        callback(...args);
      }, delay);
      
      this.timers.add(timerId);
      return timerId;
    },

    // Track setInterval
    setInterval(callback, interval, ...args) {
      const intervalId = w.setInterval(callback, interval, ...args);
      this.intervals.add(intervalId);
      return intervalId;
    },

    // Track requestAnimationFrame
    requestAnimationFrame(callback) {
      const rafId = w.requestAnimationFrame((timestamp) => {
        this.animationFrames.delete(rafId);
        callback(timestamp);
      });
      
      this.animationFrames.add(rafId);
      return rafId;
    },

    // Clear tracked timer
    clearTimeout(timerId) {
      w.clearTimeout(timerId);
      this.timers.delete(timerId);
    },

    // Clear tracked interval
    clearInterval(intervalId) {
      w.clearInterval(intervalId);
      this.intervals.delete(intervalId);
    },

    // Clear tracked animation frame
    cancelAnimationFrame(rafId) {
      w.cancelAnimationFrame(rafId);
      this.animationFrames.delete(rafId);
    },

    // ================================================================
    // EVENT LISTENER MANAGEMENT
    // ================================================================
    
    // Track event listener
    addEventListener(element, type, listener, options) {
      if (!element || !type || !listener) return;
      
      // Add the listener
      element.addEventListener(type, listener, options);
      
      // Track it
      if (!this.listeners.has(element)) {
        this.listeners.set(element, []);
      }
      
      this.listeners.get(element).push({
        type,
        listener,
        options
      });
    },

    // Remove and untrack event listener
    removeEventListener(element, type, listener, options) {
      if (!element || !type || !listener) return;
      
      element.removeEventListener(type, listener, options);
      
      const elementListeners = this.listeners.get(element);
      if (elementListeners) {
        const index = elementListeners.findIndex(l => 
          l.type === type && l.listener === listener
        );
        if (index !== -1) {
          elementListeners.splice(index, 1);
        }
        
        // Clean up empty arrays
        if (elementListeners.length === 0) {
          this.listeners.delete(element);
        }
      }
    },

    // Remove all listeners for an element
    removeAllListeners(element) {
      const elementListeners = this.listeners.get(element);
      if (elementListeners) {
        elementListeners.forEach(({ type, listener, options }) => {
          element.removeEventListener(type, listener, options);
        });
        this.listeners.delete(element);
      }
    },

    // ================================================================
    // OBSERVER MANAGEMENT
    // ================================================================
    
    // Track observer
    trackObserver(observer) {
      if (observer && typeof observer.disconnect === 'function') {
        this.observers.add(observer);
        return observer;
      }
      return null;
    },

    // Untrack observer
    untrackObserver(observer) {
      this.observers.delete(observer);
    },

    // ================================================================
    // CLEANUP OPERATIONS
    // ================================================================
    
    // Clean up old/unused resources
    cleanup(options = {}) {
      const {
        force = false,
        maxAge = this.limits.maxBlobAge,
        maxBlobs = this.limits.maxBlobs
      } = options;
      
      const now = Date.now();
      let cleaned = 0;
      
      cLog('🧹 Starting memory cleanup...');
      
      // Clean old blobs
      const blobsToRemove = [];
      for (const [url, blobInfo] of this.blobs) {
        const age = now - blobInfo.created;
        
        if (force || age > maxAge || this.blobs.size > maxBlobs) {
          blobsToRemove.push(url);
        }
      }
      
      blobsToRemove.forEach(url => {
        if (this.revokeBlob(url)) cleaned++;
      });
      
      // Update metrics
      this.metrics.totalCleanups++;
      this.metrics.lastCleanup = now;
      
      cLog(`✅ Cleanup complete: ${cleaned} blobs removed, ${this.blobs.size} remaining`);
      
      return {
        blobsRemoved: cleaned,
        blobsRemaining: this.blobs.size,
        totalMemoryUsed: this.metrics.totalMemoryUsed
      };
    },

    // Force cleanup of all resources
    cleanupAll() {
      cLog('🧨 Force cleanup of ALL resources...');
      
      const result = this.cleanup({ force: true });
      
      // Remove any remaining listeners
      this.listeners.forEach((listeners, element) => {
        this.removeAllListeners(element);
      });
      
      cLog('💥 All resources cleaned up');
      return result;
    },

    // ================================================================
    // MEMORY MONITORING
    // ================================================================
    
    // Check memory limits and warn/cleanup if needed
    _checkMemoryLimits() {
      const memoryMB = this.metrics.totalMemoryUsed / (1024 * 1024);
      
      if (memoryMB > this.limits.maxMemoryMB) {
        this.metrics.memoryWarnings++;
        console.warn(`⚠️ Memory usage high: ${memoryMB.toFixed(1)}MB`);
        this.cleanup({ maxAge: this.limits.maxBlobAge / 2 });
      }
      
      if (this.blobs.size > this.limits.maxBlobs) {
        console.warn(`⚠️ Too many blobs: ${this.blobs.size}/${this.limits.maxBlobs}`);
        this.cleanup({ maxBlobs: this.limits.maxBlobs * 0.8 });
      }
    },

    // Get memory status
    getMemoryStatus() {
      const now = Date.now();
      
      return {
        blobs: {
          count: this.blobs.size,
          totalSize: this.metrics.totalMemoryUsed,
          totalSizeMB: this.metrics.totalMemoryUsed / (1024 * 1024),
          oldestAge: this.blobs.size > 0 ? Math.min(...Array.from(this.blobs.values()).map(b => now - b.created)) : 0,
          newestAge: this.blobs.size > 0 ? Math.max(...Array.from(this.blobs.values()).map(b => now - b.created)) : 0
        },
        timers: {
          timeouts: this.timers.size,
          intervals: this.intervals.size,
          animationFrames: this.animationFrames.size
        },
        listeners: {
          elements: this.listeners.size,
          total: Array.from(this.listeners.values()).reduce((sum, arr) => sum + arr.length, 0)
        },
        observers: this.observers.size,
        metrics: { ...this.metrics },
        limits: { ...this.limits }
      };
    },

    // Log memory status
    logMemoryStatus() {
      const status = this.getMemoryStatus();
      
      console.group('📊 Pg Memory Status');
      cLog(`🔗 Blobs: ${status.blobs.count} (${status.blobs.totalSizeMB.toFixed(1)}MB)`);
      cLog(`⏰ Timers: ${status.timers.timeouts + status.timers.intervals + status.timers.animationFrames}`);
      cLog(`👂 Listeners: ${status.listeners.total} on ${status.listeners.elements} elements`);
      cLog(`👁️ Observers: ${status.observers}`);
      cLog(`🧹 Cleanups: ${status.metrics.totalCleanups}`);
      cLog(`⚠️ Warnings: ${status.metrics.memoryWarnings}`);
      console.groupEnd();
      
      return status;
    },

    // ================================================================
    // UTILITY METHODS
    // ================================================================
    
    // Generate unique blob ID
    _generateBlobId() {
      const utils = Pg.Utils;
      const makeId = utils?.makeId || function() { 
        return Math.random().toString(36).substr(2, 8); 
      };
      return `blob_${Date.now()}_${makeId(6)}`;
    },

    // Format file size
    _formatSize(bytes) {
      const utils = Pg.Utils;
      if (utils?.formatFileSize) {
        return utils.formatFileSize.call(utils, bytes);
      }
      
      // Fallback implementation
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
    },

    // Initialize auto-cleanup
    init() {
      cLog('🧠 Initializing Pg Memory Manager...');
      
      // Set up periodic cleanup
      this.cleanupIntervalId = setInterval(() => {
        this.cleanup();
      }, this.limits.cleanupInterval);
      
      // Cleanup on page unload
      w.addEventListener('beforeunload', () => {
        this.cleanup({ force: true });
      });
      
      cLog('✅ Pg Memory Manager ready');
    },

    // ================================================================
    // INITIALIZATION & MODULE UTILITIES
    // ================================================================
    
    // Log module info
    logInfo() {
      console.groupCollapsed('📦 Pg.Memory module loaded - Available features:');
      console.log('🧠 Memory management features:', [
        'Blob URL tracking',
        'Auto-cleanup',
        'Memory monitoring'
      ]);
      console.log(Object.keys(this).filter(key => typeof this[key] === 'function'));
      console.groupEnd();
    }
  };

  // Auto-initialize
  Pg.Memory.init();

  // Mark module as loaded
  if (Pg.moduleStatus) {
    Pg.moduleStatus.memory = true;
    Pg.checkModules?.();
  }

  // Auto-log on load (can be disabled)
  if (typeof w !== 'undefined' && !Pg._silentLoad) {
    Pg.Memory.logInfo();
  }

})(window); 
